import { classnames } from '@/helpers';
import { TimeSlotProps } from './TimeSlot.types';

/**
 * Shared selectable time-slot button for the weekpicker pick-time views.
 * Consumed by the calendar/week/month views.
 *
 * - Default / Reduced price: time over price, `reduced` shows its discounted
 *   price in the highlight colour. Hover and pressed are CSS pseudo-states;
 *   `selected` is the primary-filled state.
 * - SlotsLeft: the group-booking card — the time/price slot with a
 *   primary-filled "spots left" badge fused to its bottom edge. Rendered
 *   whenever `capacity` is provided; `selected` maps to the pressed
 *   primary-fill visual.
 *
 * Purely presentational — it owns no availability logic and renders the labels
 * handed to it via props.
 */
const TimeSlot = ({
  time,
  type = 'default',
  selected = false,
  disabled = false,
  price,
  capacity,
  isNarrow = false,
  className,
  onClick,
}: TimeSlotProps) => {
  const isReduced = type === 'reduced';

  // SlotsLeft (group-booking) variant. The time/price slot sits above a
  // primary-filled "X kvar" badge; rendered whenever a capacity label is supplied. The time
  // uses text-m here (vs text-l on the regular slot) to match the more compact
  // card.
  if (capacity) {
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-pressed={selected}
        className={classnames(
          'shadow-xs group flex w-full flex-col overflow-hidden rounded-sm text-center transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className,
        )}>
        {/* Time + price — min 36px tall; border on l/r/t only, the badge butts against the bottom. */}
        <div
          className={classnames(
            'px-2xs pt-2xs pb-3xs flex min-h-[36px] w-full flex-col items-center justify-center rounded-t-sm border border-b-0 border-solid transition-colors',
            selected
              ? 'border-bg_primary bg-bg_primary'
              : 'border-border_bold bg-bg_secondary group-hover:border-border_selected',
          )}>
          <span className={classnames('text-m', selected ? 'text-fg_on_primary' : 'text-fg_primary')}>{time}</span>
          {price && (
            <span
              className={classnames(
                'text-xs',
                selected ? 'text-fg_secondary_on_primary' : isReduced ? 'text-fg_highlight' : 'text-fg_secondary',
              )}>
              {price}
            </span>
          )}
        </div>
        {/* Spots-left badge — 8px is a deliberate sub-token size for the compact pill. */}
        <span
          className={classnames(
            'p-3xs bg-bg_primary flex w-full items-center justify-center rounded-b-sm text-center text-[8px]',
            selected ? 'text-fg_secondary_on_primary' : 'text-fg_on_primary',
          )}>
          {capacity}
        </span>
      </button>
    );
  }

  const stateClasses = selected
    ? 'border-bg_primary bg-bg_primary text-fg_on_primary'
    : 'border-border_bold bg-bg_secondary text-fg_primary shadow-xs hover:border-border_selected hover:shadow-sm active:border-bg_primary active:bg-bg_primary active:text-fg_on_primary';

  // The labels carry their own colour, which beats the colour they would inherit
  // from the button, so the pressed fill's inversion has to be restated here as a
  // group-active variant rather than left to `active:text-*` on the button.
  const timeColor = selected ? 'text-fg_on_primary' : 'text-fg_primary group-active:text-fg_on_primary';
  const priceColor = selected
    ? 'text-fg_on_primary'
    : isReduced
    ? 'text-fg_highlight group-active:text-fg_on_primary'
    : 'text-fg_secondary group-active:text-fg_on_primary';

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={classnames(
        'p-2xs group flex min-h-[40px] w-full flex-col items-center justify-center rounded-sm border border-solid text-center transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        !disabled && stateClasses,
        disabled && 'border-border_regular bg-bg_secondary text-fg_disabled',
        className,
      )}>
      <span className={classnames(isNarrow ? 'text-m' : 'text-l', timeColor)}>{time}</span>
      {price && <span className={classnames('text-xs', priceColor)}>{price}</span>}
    </button>
  );
};

export default TimeSlot;
